import {
  CategoryOutlined,
  FormatQuoteOutlined,
  AltRouteOutlined,
  VerifiedOutlined,
  FactCheckOutlined,
  FolderOutlined,
} from "@mui/icons-material";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from "react";
import { useIsAdmin } from "src/helpers/useIsAdmin";
import { useGetUserSettings } from "src/views/users/SettingsService";
import { useUIStore } from "../../stores/UIStore";
import { useChatStore } from "../../stores/ChatStore";
import { useOnboardingStore } from "../../stores/OnboardingStore";
import SourcesTab from "src/reusable/Sidebar/tabs/SourcesTab/SourcesTab";
import ArtifactsTab from "src/reusable/Sidebar/tabs/ArtifactsTab/ArtifactsTab";
import RoutingTab from "src/reusable/Sidebar/tabs/RoutingTab/RoutingTab";
import SourcesDownloadButton from "../sources/SourcesDownloadButton";
import SourcesViewToggle from "../sources/SourcesViewToggle";
import AutoOpenToggle from "src/reusable/Sidebar/AutoOpenToggle";
import VerificationPane from "../onboarding/VerificationPane";
import FfnRecordPanel from "./FfnRecordPanel";
import { useFfnRecordStore } from "../../stores/FfnRecordStore";
import WorkspaceFileExplorer from "../../workspace/WorkspaceFileExplorer";
import { SidebarConfig } from "src/reusable/Sidebar/types";
import { RoutingMetadata } from "../../routing/types";

interface AtomAssistSidebarContext {
  responseSources: any[];
  artifacts: Record<string, any>;
  routing: RoutingMetadata | null;
  viewMode: "dataChunk" | "cardList";
  setViewMode: Dispatch<SetStateAction<"dataChunk" | "cardList">>;
  detailSourceViewSdrId: number | null;
  detailSourceViewSourceId: number | null;
  setDetailSourceViewSdrId: Dispatch<SetStateAction<number | null>>;
  setDetailSourceViewSourceId: Dispatch<SetStateAction<number | null>>;
  /** Dialogue + message ids for the source-provenance funnel fetch. */
  dialogueId: number | null;
  messageId: string | null;
  /** Total cited count across the session, for the Sources badge (#20423 Story 3). */
  citedCount?: number;
}

/**
 * Verification-tab badge count must match what {@link VerificationPane}
 * actually renders. Today the pane renders exactly one card — the profile
 * form (gated on ``profileFormFields``). The earlier counter that summed
 * profileArtifact + profileFormFields + answerDetails over-reported (e.g.
 * "2 items" right after FTUX completion when only the profile card was
 * visible) because profileArtifact and answerDetails are stored but never
 * rendered as separate cards.
 *
 * Exported so the unit test can pin down the contract without rendering
 * the full sidebar tree.
 */
export const computeVerificationBadgeCount = (state: {
  profileFormFields: { length: number } | null | undefined;
}): number => {
  const fields = state.profileFormFields;
  return fields && fields.length > 0 ? 1 : 0;
};

/**
 * useAtomAssistSidebar - Configuration hook for AtomAssist sidebar
 *
 * Defines sidebar tabs, state management, and responsive behavior
 * Returns a SidebarConfig that can be used with UnifiedSidebar component
 */
export const useAtomAssistSidebar =
  (): SidebarConfig<AtomAssistSidebarContext> => {
    const { preferences, setRightSidebar } = useUIStore();
    const autoOpenEnabled = preferences?.autoOpenSidebar ?? true;
    const isAdmin = useIsAdmin();
    // Opt-in per-user setting; missing reads as off (same gate as the nav).
    const { data: userSettings } = useGetUserSettings();
    const showWorkspace = Boolean(userSettings?.show_agent_workspace);
    const isOnboarding = useOnboardingStore((s) => s.isOnboarding);
    const ffnRecord = useFfnRecordStore((s) => s.record);
    const hasFfnRecord = ffnRecord !== null;
    const ffnBadge = hasFfnRecord
      ? (ffnRecord?.challenge_failed_items?.length ?? 0) || 1
      : 0;

    // When a 50.59 record first appears (and auto-open is on), widen the
    // sidebar to half the screen — the record panel is a working surface,
    // not a peek. The user can still resize; we only set it on appearance.
    const prevHasFfnRecord = useRef(false);
    useEffect(() => {
      if (hasFfnRecord && !prevHasFfnRecord.current && autoOpenEnabled) {
        setRightSidebar({
          width: Math.max(500, Math.floor(window.innerWidth / 2)),
        });
      }
      prevHasFfnRecord.current = hasFfnRecord;
    }, [hasFfnRecord, autoOpenEnabled, setRightSidebar]);

    return useMemo(
      () => ({
        tabs: [
          ...(hasFfnRecord
            ? [
                {
                  id: "ffn-record" as const,
                  label: "50.59",
                  headerLabel: "50.59 Record",
                  icon: FactCheckOutlined,
                  renderContent: () => <FfnRecordPanel />,
                  getBadgeContent: () => ffnBadge,
                },
              ]
            : []),
          ...(isOnboarding
            ? [
                {
                  id: "verification" as const,
                  label: "Verification",
                  headerLabel: "Verification Pane",
                  icon: VerifiedOutlined,
                  renderContent: () => <VerificationPane />,
                  getBadgeContent: () =>
                    computeVerificationBadgeCount(
                      useOnboardingStore.getState(),
                    ),
                },
              ]
            : []),
          {
            id: "sources",
            label: "Sources",
            icon: FormatQuoteOutlined,
            renderContent: ({ context }) => {
              if (!context) return null;
              return (
                <SourcesTab
                  sources={context.responseSources}
                  viewMode={context.viewMode}
                  setViewMode={context.setViewMode}
                  detailSourceViewSdrId={context.detailSourceViewSdrId}
                  detailSourceViewSourceId={context.detailSourceViewSourceId}
                  setDetailSourceViewSdrId={context.setDetailSourceViewSdrId}
                  setDetailSourceViewSourceId={
                    context.setDetailSourceViewSourceId
                  }
                  dialogueId={context.dialogueId}
                  messageId={context.messageId}
                />
              );
            },
            renderHeaderActions: ({ context }) => {
              if (!context) return null;
              return (
                <>
                  <SourcesDownloadButton
                    responseSources={context.responseSources}
                  />
                  <SourcesViewToggle
                    alignment={context.viewMode}
                    handleAlignment={(_, newValue) => {
                      if (newValue) {
                        context.setViewMode(
                          newValue as "dataChunk" | "cardList",
                        );
                      }
                    }}
                  />
                  <AutoOpenToggle />
                </>
              );
            },
            getBadgeContent: (context) => {
              // Prefer the session-accumulated cited count (the provenance
              // funnel's total); fall back to the legacy flat source list.
              return (
                context?.citedCount ?? context?.responseSources.length ?? 0
              );
            },
          },
          {
            id: "artifacts",
            label: "Artifacts",
            icon: CategoryOutlined,
            renderContent: ({ context }) => {
              if (!context) return null;
              return <ArtifactsTab artifacts={context.artifacts} />;
            },
            renderHeaderActions: () => <AutoOpenToggle />,
            getBadgeContent: (context) => {
              return context?.artifacts
                ? Object.keys(context.artifacts).length
                : 0;
            },
          },
          ...(showWorkspace
            ? [
                {
                  id: "workspace" as const,
                  label: "Agent workspace",
                  icon: FolderOutlined,
                  renderContent: () => <WorkspaceFileExplorer compact />,
                  getBadgeContent: () => 0,
                },
              ]
            : []),
          ...(isAdmin
            ? [
                {
                  id: "routing" as const,
                  label: "Routing",
                  headerLabel: "Agent Routing",
                  icon: AltRouteOutlined,
                  renderContent: ({
                    context,
                  }: {
                    context?: AtomAssistSidebarContext;
                  }) => {
                    if (!context) return null;
                    return <RoutingTab routing={context.routing} />;
                  },
                  getBadgeContent: (context?: AtomAssistSidebarContext) => {
                    if (
                      context?.routing &&
                      context.routing.persona_slug !== "general-chat"
                    )
                      return 1;
                    const { persona } = useChatStore.getState();
                    if (persona?.slug) return 1;
                    return 0;
                  },
                },
              ]
            : []),
        ],
        defaultTab: isOnboarding ? "verification" : "sources",
        responsive: {
          enableMobile: true,
          enableDesktopNav: true,
          enableResize: true,
          minWidth: 300,
          maxWidth: Math.max(
            800,
            typeof window !== "undefined"
              ? Math.floor(window.innerWidth * 0.6)
              : 800,
          ),
          defaultWidth: 400,
        },
        autoOpen: {
          enabled: () => autoOpenEnabled,
          getContentCounts: (context) => {
            return {
              sources: context?.responseSources.length ?? 0,
              artifacts: context?.artifacts
                ? Object.keys(context.artifacts).length
                : 0,
              "ffn-record": hasFfnRecord ? 1 : 0,
            };
          },
          // Sources arriving auto-opens the sidebar focused on the Sources
          // tab (the three-tier funnel is the primary provenance signal).
          // UnifiedSidebar only auto-selects while the sidebar is CLOSED, so
          // a user who is already browsing another tab is never yanked off it.
          autoSelectTab: hasFfnRecord
            ? "ffn-record"
            : isOnboarding
              ? "verification"
              : "sources",
          triggerKey: hasFfnRecord
            ? "ffn-record"
            : isOnboarding
              ? "verification"
              : "sources",
        },
      }),
      [
        autoOpenEnabled,
        isAdmin,
        showWorkspace,
        isOnboarding,
        hasFfnRecord,
        ffnBadge,
      ],
    );
  };
